var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement } from 'lit';
import { customElement, property, query, queryAll, state } from 'lit/decorators.js';
import styles from './header.styles';
import { classMap } from 'lit/directives/class-map.js';
import { dictionary } from '@mf-cms/helpers';
let PHeader = class PHeader extends LitElement {
    constructor() {
        super(...arguments);
        this.layout = 'default';
        this.navAlign = 'right';
        this.menu = [];
        this.cultures = [];
        this.searchExpanded = false;
        this.culture = 'en-GB';
        this.showUser = false;
        this.siteType = '';
        this.languageListExpanded = false;
        this.userListExpanded = false;
        this.mobileNavExpanded = false;
    }
    static { this.styles = styles; }
    /**
     * Connected callback lifecycle
     * @return {void}
     */
    connectedCallback() {
        super.connectedCallback();
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
    }
    /**
     * Handle outside clicks
     * @param {MouseEvent} event - event
     * @return {void}
     */
    handleDocumentClick(event) {
        const path = event.composedPath();
        // Close language list if clicked outside
        if (!path.includes(this.langNav) && this.languageListExpanded) {
            this.languageListExpanded = false;
            document.removeEventListener('mousedown', this.handleDocumentClick);
        }
        // Close user list if clicked outside
        if (!path.includes(this.userNav) && this.userListExpanded) {
            this.userListExpanded = false;
            document.removeEventListener('mousedown', this.handleDocumentClick);
        }
    }
    /**
     * Toggle language list
     * @return {void}
     */
    toggleLanguageList() {
        if (this.languageListExpanded) {
            document.removeEventListener('mousedown', this.handleDocumentClick);
        }
        else {
            document.addEventListener('mousedown', this.handleDocumentClick);
            // Close open submenus
            this.navExpandButtons.forEach((button) => {
                button.setAttribute('aria-expanded', 'false');
            });
            this.navExpandLinklessButtons.forEach((button) => {
                button.setAttribute('aria-expanded', 'false');
            });
        }
        this.languageListExpanded = !this.languageListExpanded;
    }
    /**
     * Toggle user list
     * @param {Event} e - Event
     * @return {void}
     */
    toggleUserList(e) {
        if (this.userListExpanded) {
            document.removeEventListener('mousedown', this.handleDocumentClick);
        }
        else {
            document.addEventListener('mousedown', this.handleDocumentClick);
            // Close open submenus
            this.navExpandButtons.forEach((button) => {
                button.setAttribute('aria-expanded', 'false');
            });
            this.navExpandLinklessButtons.forEach((button) => {
                button.setAttribute('aria-expanded', 'false');
            });
        }
        this.userListExpanded = !this.userListExpanded;
    }
    /**
     * Toggle submenu
     * @param {Event} e - Event
     * @return {void}
     */
    toggleSubMenu(e) {
        const button = e.target;
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    }
    /**
     * Close all submenus on focus out
     * @param {any} e - Event
     * @return {void}
     */
    subMenuFocusOut(e) {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            this.navExpandButtons.forEach((button) => {
                button.setAttribute('aria-expanded', 'false');
            });
            this.navExpandLinklessButtons.forEach((button) => {
                button.setAttribute('aria-expanded', 'false');
            });
        }
    }
    /**
     * Toggle search
     * @return {void}
     */
    toggleSearch() {
        // Focus search input if expanding
        if (!this.searchExpanded) {
            this.mobileNavExpanded = false;
            setTimeout(() => {
                if (this.searchInput) {
                    this.searchInput.setFocus();
                }
            }, 10);
        }
        this.searchExpanded = !this.searchExpanded;
    }
    /**
     * Toggle mobile navigation
     * @return {void}
     */
    toggleMobileNavigation() {
        // Set focus
        if (!this.mobileNavExpanded) {
            setTimeout(() => {
                this.mainNav.querySelector('a')?.focus();
            }, 10);
        }
        this.mobileNavExpanded = !this.mobileNavExpanded;
    }
    /**
     * Get active culture
     * @return {Culture} Active culture
     **/
    getActiveCulture() {
        return this.cultures.find((x) => x.active) ?? this.cultures[0];
    }
    /**
     * Handle keydown
     * @param {KeyboardEvent} e - event
     * @return {void}
     */
    handleKeydown(e) {
        if (e.key === 'Escape') {
            this.userListExpanded = false;
            this.languageListExpanded = false;
            this.navExpandButtons.forEach((button) => {
                button.setAttribute('aria-expanded', 'false');
            });
            this.navExpandLinklessButtons.forEach((button) => {
                button.setAttribute('aria-expanded', 'false');
            });
        }
    }
    /**
     * Trigger search
     * @param {KeyboardEvent} e - event
     * @return {void}
     */
    triggerSearch(e) {
        if (e.key === 'Enter') {
            this.searchForm.submit();
        }
    }
    /**
     * Get Menu page Children Items
     *
     * @param {MenuItem} child - Menu item to iterate over
     * @returns {*} - html
     */
    getMenuPageChildrenItems(child) {
        return html ` <li class=${classMap({ selected: child.active, [`has-sub`]: child.children && child.children.length > 0
        })}
      >
        <a aria-current=${child.active ? 'page' : 'false'} href=${child.url}> ${child.name} </a>
        ${child.children && child.children.length > 0
            ? html ` <button class="expand-sub" aria-label=${dictionary(this.culture, 'header', 'expandSubmenu', [child.name])} aria-expanded="false" aria-controls=${'sub-' + child.guid} @click=${this.toggleSubMenu} > <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="11" height="11" fill="currentColor" class="caret" viewBox="0 0 16 16" > <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" /> </svg> </button> <ul id="sub-${child.guid}"> ${child.children.map((child2) => this.getMenuPageChildrenItems(child2))} </ul> `
            : ''}
      </li>
    `;
    }
    /**
     * Render component
     * @returns {TemplateResult} Template
     */
    render() {
        const classes = {
            [`${this.layout}-layout`]: true,
            [`nav-${this.navAlign}`]: true,
            contained: this.contained,
            edit: this.editMode
        };
        return html ` <header class=${classMap(classes)} @keydown=${this.handleKeydown}> <a href="#content" class="skip-to-content" >${dictionary(this.culture, 'header', 'skipLink')}</a > <div class="wrapper"> <div class="logo"> ${this.logo ? html `
                  <a href=${this.getActiveCulture()?.url}>
                    <img src=${this.logo} alt="Logo" />
                  </a>
                `
            : ''}
          </div>
          ${this.renderNavigation()} ${this.renderOptionsArea()}

          <div class="search${classMap({ show: this.searchExpanded })}" id="search">
            <div class="search-wrap" role="search">
              <form action=${this.searchPage} method="get" id="search-form">
                <!-- special solution to be able to post the value with a form since shadow dom input does not work in form
                           we could also do a search input component with embeded form or not use a component for inputs -->
                <input type="hidden" value=${this.searchQuery} id="search-value" name="q" />
                <p-input
                  value=${this.searchQuery}
                  @keypress=${(e) => this.triggerSearch(e)}
                  @p-input=${() => (this.searchValue.value = this.searchInput.value)}
                  type="search"
                  label-hidden
                  placeholder=${this.siteType == 'play'
            ? dictionary(this.culture, 'header', 'searchPlaceholderVideo')
            : dictionary(this.culture, 'header', 'searchPlaceholder')}
                  label=${dictionary(this.culture, 'header', 'search')}
                  input-id="main-search"
                  id="search-input"
                >
                  <svg
                    slot="prefix"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    class="bi bi-search"
                    viewBox="0 0 16 16"
                  >
                    <path
                      d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"
                    />
                  </svg>
                </p-input>
              </form>
            </div>
          </div>
        </div>
      </header>
    `;
    }
    renderOptionsArea() {
        return html ` <nav class="options" aria-label="Options"> <ul> ${this.renderLanguageSelect()} ${this.showUser ? html `
                <li class="user-li">
                  <button
                    @click=${this.toggleUserList}
                    aria-expanded=${this.userListExpanded ? 'true' : 'false'}
                    aria-label=${dictionary(this.culture, 'header', 'loggedInAs')}
                    aria-controls="user-nav"
                  >
                    <svg
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="19"
                      height="19"
                      fill="currentColor"
                      class="bi bi-person"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6m2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0m4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4m-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10s-3.516.68-4.168 1.332c-.678.678-.83 1.418-.832 1.664z"
                      />
                    </svg>
                    <svg
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="11"
                      height="11"
                      fill="currentColor"
                      class="caret"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
                      />
                    </svg>
                  </button>
                  <ul
                    id="user-nav"
                    @focusout=${(e) => (this.userListExpanded = e.currentTarget.contains(e.relatedTarget))}
                  >
                    <li>
                      <a href="/logout">${dictionary(this.culture, 'header', 'logout')}</a>
                    </li>
                  </ul>
                </li>
              `
            : ''}
          ${this.layout === 'default'
            ? html ` <li> <button @click=${this.toggleSearch} aria-label=${dictionary(this.culture, 'header', 'showSearch')} aria-expanded=${this.searchExpanded ? 'true' : 'false'} aria-controls="search" > <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-search" viewBox="0 0 16 16" > <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0" /> </svg> </button> </li> `
            : ''}
          ${this.collectionItems > 0
            ? html ` <li> <button aria-label=${`${dictionary(this.culture, 'header', 'collection')}, ${this.collectionItems} ${this.collectionItems > 1 ? dictionary(this.culture, 'header', 'items') : dictionary(this.culture, 'header', 'item')}`}
                  >
                    <svg
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      class="bi bi-bag"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M8 1a2.5 2.5 0 0 1 2.5 2.5V4h-5v-.5A2.5 2.5 0 0 1 8 1m3.5 3v-.5a3.5 3.5 0 1 0-7 0V4H1v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4zM2 5h12v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z"
                      />
                    </svg>
                    <span aria-hidden="true" class="collection-count">${this.collectionItems}</span>
                  </button>
                </li>
              `
            : ''}
          <li class="mobile-nav-li">
            <button
              @click=${this.toggleMobileNavigation}
              class="toggle-mobile-nav"
              aria-label=${dictionary(this.culture, 'header', 'showMenu')}
              aria-expanded=${this.mobileNavExpanded ? 'true' : 'false'}
            ></button>
          </li>
        </ul>
      </nav>
    `;
    }
    renderLanguageSelect() {
        if (!this.cultures || this.cultures.length <= 1)
            return html ``;
        return html ` <li class="lang-li"> <button @click=${this.toggleLanguageList} aria-expanded=${this.languageListExpanded ? 'true' : 'false'} aria-controls="lang-nav" aria-label=${`${dictionary(this.culture, 'header', 'selectedLanguage')} ${this.getActiveCulture()?.name}`}
          class="lang"
        >
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            class="bi bi-globe"
            viewBox="0 0 16 16"
          >
            <path
              d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8m7.5-6.923c-.67.204-1.335.82-1.887 1.855A8 8 0 0 0 5.145 4H7.5zM4.09 4a9.3 9.3 0 0 1 .64-1.539 7 7 0 0 1 .597-.933A7.03 7.03 0 0 0 2.255 4zm-.582 3.5c.03-.877.138-1.718.312-2.5H1.674a7 7 0 0 0-.656 2.5zM4.847 5a12.5 12.5 0 0 0-.338 2.5H7.5V5zM8.5 5v2.5h2.99a12.5 12.5 0 0 0-.337-2.5zM4.51 8.5a12.5 12.5 0 0 0 .337 2.5H7.5V8.5zm3.99 0V11h2.653c.187-.765.306-1.608.338-2.5zM5.145 12q.208.58.468 1.068c.552 1.035 1.218 1.65 1.887 1.855V12zm.182 2.472a7 7 0 0 1-.597-.933A9.3 9.3 0 0 1 4.09 12H2.255a7 7 0 0 0 3.072 2.472M3.82 11a13.7 13.7 0 0 1-.312-2.5h-2.49c.062.89.291 1.733.656 2.5zm6.853 3.472A7 7 0 0 0 13.745 12H11.91a9.3 9.3 0 0 1-.64 1.539 7 7 0 0 1-.597.933M8.5 12v2.923c.67-.204 1.335-.82 1.887-1.855q.26-.487.468-1.068zm3.68-1h2.146c.365-.767.594-1.61.656-2.5h-2.49a13.7 13.7 0 0 1-.312 2.5m2.802-3.5a7 7 0 0 0-.656-2.5H12.18c.174.782.282 1.623.312 2.5zM11.27 2.461c.247.464.462.98.64 1.539h1.835a7 7 0 0 0-3.072-2.472c.218.284.418.598.597.933M10.855 4a8 8 0 0 0-.468-1.068C9.835 1.897 9.17 1.282 8.5 1.077V4z"
            />
          </svg>
          <span aria-hidden="true"> ${this.getActiveCulture()?.name} </span>
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            fill="currentColor"
            class="caret"
            viewBox="0 0 16 16"
          >
            <path
              fill-rule="evenodd"
              d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
            />
          </svg>
        </button>
        <ul
          id="lang-nav"
          @focusout=${(e) => (this.languageListExpanded = e.currentTarget.contains(e.relatedTarget))}
        >
          ${this.renderLanguageOptions()}
        </ul>
      </li>
    `;
    }
    renderLanguageOptions() {
        return this.cultures.map((culture) => {
            return html ` <li class=${classMap({ selected: culture.active })}> <a aria-current=${culture.active ? 'page' : 'false'} href=${culture.url} >${culture.name}</a > </li> `;
        });
    }
    renderNavigation() {
        return html ` <nav class="nav${classMap({ show: this.mobileNavExpanded })}" aria-label=${dictionary(this.culture, 'header', 'mainMenu')} > <ul> ${this.renderNavigationItems()} </ul> </nav> `;
    }
    renderNavigationItems() {
        return this.menu.map((page) => html ` <li class=${classMap({ selected: page.active, [`linkless`]: !!page.pageType && page.pageType == 'videos',
            [`has-sub`]: page.children && page.children.length > 0
        })}
        >
          ${this.renderNavigationLink(page)}
          ${page.children && page.children.length > 0
            ? html ` <ul id="sub-${page.guid}" @focusout=${this.subMenuFocusOut}> ${page.children.map((child) => this.getMenuPageChildrenItems(child))} </ul> `
            : ''}
        </li>
      `);
    }
    renderNavigationLink(page) {
        if (page.pageType == 'videos') {
            return html ` <button class="linkless-expand" aria-label=${dictionary(this.culture, 'header', 'expandSubmenu', [page.name])} aria-expanded="false" aria-current=${page.active} aria-controls=${'sub-' + page.guid} @click=${this.toggleSubMenu} > ${page.name} <span class="caret-container"> <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="11" height="11" fill="currentColor" class="caret" viewBox="0 0 16 16" > <path fill-rule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" /> </svg> </span> </button> `;
        }
        const url = page.type == 'root' && !page.isDefaultCulture ? `/${this.culture}` : page.url;
        return html ` <a href=${url} aria-current=${page.active ? 'page' : 'false'}> ${page.name} </a> ${page.children && page.children.length > 0 ? html `
            <button
              class="expand-sub"
              aria-label=${dictionary(this.culture, 'header', 'expandSubmenu', [page.name])}
              aria-expanded="false"
              aria-controls=${'sub-' + page.guid}
              @click=${this.toggleSubMenu}
            >
              <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="11"
                height="11"
                fill="currentColor"
                class="caret"
                viewBox="0 0 16 16"
              >
                <path
                  fill-rule="evenodd"
                  d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
                />
              </svg>
            </button>
          `
            : ''}
    `;
    }
};
__decorate([
    property({ type: String, reflect: true })
], PHeader.prototype, "layout", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'nav-align' })
], PHeader.prototype, "navAlign", void 0);
__decorate([
    property({ type: String, reflect: true })
], PHeader.prototype, "logo", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], PHeader.prototype, "contained", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'edit-mode' })
], PHeader.prototype, "editMode", void 0);
__decorate([
    property({ type: Number, reflect: true, attribute: 'collection-items' })
], PHeader.prototype, "collectionItems", void 0);
__decorate([
    property({ type: Array, reflect: true })
], PHeader.prototype, "menu", void 0);
__decorate([
    property({ type: Array, reflect: true })
], PHeader.prototype, "cultures", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'search-page' })
], PHeader.prototype, "searchPage", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'search-query' })
], PHeader.prototype, "searchQuery", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'search-expanded' })
], PHeader.prototype, "searchExpanded", void 0);
__decorate([
    property({ type: String })
], PHeader.prototype, "culture", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'show-user' })
], PHeader.prototype, "showUser", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'site-type' })
], PHeader.prototype, "siteType", void 0);
__decorate([
    state()
], PHeader.prototype, "languageListExpanded", void 0);
__decorate([
    state()
], PHeader.prototype, "userListExpanded", void 0);
__decorate([
    state()
], PHeader.prototype, "mobileNavExpanded", void 0);
__decorate([
    query('.lang-li')
], PHeader.prototype, "langNav", void 0);
__decorate([
    query('.user-li')
], PHeader.prototype, "userNav", void 0);
__decorate([
    query('.nav')
], PHeader.prototype, "mainNav", void 0);
__decorate([
    query('#search-input')
], PHeader.prototype, "searchInput", void 0);
__decorate([
    queryAll('.expand-sub')
], PHeader.prototype, "navExpandButtons", void 0);
__decorate([
    queryAll('.linkless-expand')
], PHeader.prototype, "navExpandLinklessButtons", void 0);
__decorate([
    query('#search-form')
], PHeader.prototype, "searchForm", void 0);
__decorate([
    query('#search-value')
], PHeader.prototype, "searchValue", void 0);
PHeader = __decorate([
    customElement('p-header')
    /**
     * Header component
     *
     * @class PHeader
     * @typedef {PHeader}
     * @extends {LitElement}
     */
], PHeader);
export { PHeader };
